// substance/_model.ts
export type SubstanceUsage = "ingredient" | "target" | "formulation";
export type SubstanceType = "compound" | "mixture";
export type CompositionStatus = "READY" | "ANALYZING" | "RE-ANALYZING" | "NOT_AVAILABLE";

export interface SubstanceStock {
  inStock: boolean; supplier: string; catalog: string; lot: string;
  size: string; price: number; form: string; loc: string; expires: string;
  // Drawer-facing inventory-spec facets, hydrated from InventoryRow by the
  // list adapter (fromLibraryItem.ts → stockOf) so an EXISTING lot opens with
  // its real values instead of blank. These mirror DrawerStock's extra fields;
  // `{ id?: string; raw?: string }` is structurally `TypeValue` (no import
  // needed). All optional so the dev/mock SUBSTANCE_ROWS need not set them.
  //   standard — single-select: the PRIMARY standard (id or display text).
  //   claims   — multi-select:  one entry per attached claim.
  //   currency — the price currency ({ id } when catalogued, else { raw }).
  //   storage  — storage-condition (id or raw display string).
  standard?: string;
  claims?: { id?: string; raw?: string }[];
  currency?: { id?: string; raw?: string };
  storage?: string;
}
export interface SubstanceRow {
  id: string; name: string; type: SubstanceType; family: string; code: string;
  /** Family display data carried from the server `families[]` rel (or the
   *  drawer's family picker for a just-created family) so custom families render
   *  with their real color/label, not only the fixed FAMILY_COLORS palette. */
  familyColor?: string | null;
  familyLabel?: string;
  cas: string[]; ec: string[]; fema: string[]; internal: string[];
  /** Custom-kind identifiers (pubchem/chemspider/inchikey/…). Kind stored in the
   *  server-lowercase form so hydration, snapshot, and edit diffs all match. */
  extraIds?: { kind: string; value: string }[];
  usage: SubstanceUsage[]; status: CompositionStatus;
  added: string; lastUsed: string | null; tags: string[];
  chrom: number; formula: { items: number } | null; stock: SubstanceStock | null;
}

export const USAGE_META: Record<SubstanceUsage, {
  id: SubstanceUsage; label: string; short: string; icon: string;
  bg: string; line: string; fg: string; accent: string; blurb: string;
}> = {
  ingredient:  { id: "ingredient",  label: "Ingredient",  short: "Ingredient", icon: "box",
                 bg: "var(--accent-soft)", line: "var(--accent-line)", fg: "var(--accent-ink)", accent: "var(--brand-green)",
                 blurb: "Selected & recommended in Workbench." },
  target:      { id: "target",      label: "Target",      short: "Target",     icon: "target",
                 bg: "#f6e8c8", line: "#e7c97a", fg: "#7c5813", accent: "var(--brand-amber)",
                 blurb: "Reference flavor for formulation." },
  formulation: { id: "formulation", label: "Formulation", short: "Formula",    icon: "beaker",
                 bg: "var(--lime-soft)", line: "#bcd6a0", fg: "#3c5a26", accent: "var(--brand-lime)",
                 blurb: "User-uploaded flavor recipe." },
};
export const STATUS_META: Record<CompositionStatus, { id: CompositionStatus; label: string; tone: "ok" | "working" | "todo" }> = {
  READY:          { id: "READY",          label: "Ready",             tone: "ok" },
  ANALYZING:      { id: "ANALYZING",      label: "Analyzing",         tone: "working" },
  "RE-ANALYZING": { id: "RE-ANALYZING",   label: "Re-analyzing",      tone: "working" },
  NOT_AVAILABLE:  { id: "NOT_AVAILABLE",  label: "Needs composition", tone: "todo" },
};
export const FAMILY_COLORS: Record<string, string> = {
  fruity: "var(--fam-fruity)", floral: "var(--fam-floral)", green: "var(--fam-green)",
  dairy: "var(--fam-dairy)", citrus: "var(--fam-citrus)", spicy: "var(--fam-spicy)",
  roast: "var(--fam-roast)", sweet: "var(--fam-sweet)", neutral: "var(--fam-neutral)",
};
export const FAMILY_LABELS: Record<string, string> = {
  fruity: "Fruity", floral: "Floral", green: "Green", dairy: "Dairy",
  citrus: "Citrus", spicy: "Spicy", roast: "Roast", sweet: "Sweet", neutral: "Neutral",
};

export const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso + "T00:00:00").toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";
export const relTime = (iso: string | null): string => {
  if (!iso) return "never";
  const days = Math.round((new Date(iso + "T00:00:00").getTime() - Date.now()) / 86_400_000);
  const abs = Math.abs(days);
  if (abs === 0) return "today";
  let u: string;
  if (abs < 14) u = `${abs}d`; else if (abs < 60) u = `${Math.round(abs / 7)}w`;
  else if (abs < 730) u = `${Math.round(abs / 30)}mo`; else u = `${(abs / 365).toFixed(1)}y`;
  return days < 0 ? `${u} ago` : `in ${u}`;
};
export interface IdentifierEntry { kind: "CAS" | "FEMA" | "EC" | "INT"; value: string; }
export const identifiersOf = (s: SubstanceRow): IdentifierEntry[] => [
  ...s.cas.map((value) => ({ kind: "CAS" as const, value })),
  ...s.fema.map((value) => ({ kind: "FEMA" as const, value })),
  ...s.ec.map((value) => ({ kind: "EC" as const, value })),
  ...s.internal.map((value) => ({ kind: "INT" as const, value })),
];
export const needsAttention = (s: SubstanceRow): boolean => s.status !== "READY";
